import { useState, useRef, useEffect } from 'react';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Barcode, Search, Loader2, Check, X } from 'lucide-react';

/**
 * Reusable barcode scanner input.
 * Detects USB barcode reader input (fast keystrokes ending with Enter).
 * Props:
 *  - onScan(code) — called when a barcode is scanned or Enter is pressed
 *  - result — { success, item_nome, sku } or null
 *  - loading — boolean
 *  - placeholder — input placeholder
 *  - autoFocus — boolean
 */
const BarcodeScanner = ({ onScan, result, loading, placeholder, autoFocus = false, className = '' }) => {
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  // Auto-focus after scan result
  useEffect(() => {
    if (result && inputRef.current) {
      const timer = setTimeout(() => {
        setValue('');
        inputRef.current?.focus();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [result]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && value.trim()) {
      e.preventDefault();
      onScan(value.trim());
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="relative flex gap-2">
        <div className="flex-1 relative">
          <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || "Escaneie o código de barras..."}
            className="pl-9 h-10 font-mono tracking-wider border-2 focus:border-primary"
            autoFocus={autoFocus}
            data-testid="barcode-scanner-input"
          />
        </div>
        {loading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2" />}
      </div>

      {/* Scan result feedback */}
      {result && (
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all ${
          result.success
            ? 'bg-green-50 border border-green-300 text-green-800 dark:bg-green-950/20 dark:text-green-300'
            : 'bg-red-50 border border-red-300 text-red-800 dark:bg-red-950/20 dark:text-red-300'
        }`} data-testid="barcode-scan-feedback">
          {result.success ? <Check className="h-4 w-4 shrink-0" /> : <X className="h-4 w-4 shrink-0" />}
          <span>{result.message}</span>
          {result.sku && <Badge variant="outline" className="text-xs ml-auto font-mono">{result.sku}</Badge>}
        </div>
      )}
    </div>
  );
};

export default BarcodeScanner;
